import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Flex, Text, toast, FileObjectType } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { DocumentUploadInviteStatus, DocumentStatus } from "../../../graphql/supergraphEnums";
import useGuestDocumentUpload from "../../../hooks/documents/useGuestDocumentUpload";
import { formatDate } from "../../../utils/date_formatter";
import { getErrorMessage } from "../../../utils/error_parser";
import { ButtonWrapper } from "../../common/ButtonWrapper";
import { Celebration } from "../../common/Celebration";
import {
  textColorNeutral700,
  textColorNeutral800,
  textColorNeutral900,
} from "../../common/style_vanilla.css";
import { mainContentWrapper } from "../../style_vanilla.css";
import { DocumentLists } from "../DocumentLists";
import { DocumentDetailsType, DocumentListProps } from "../DocumentLists/types";
import { DocumentUploader } from "../DocumentUploader";
import { getDocumentStatus } from "../utils";
import {
  dateDisplay,
  inviteSummaryWrapper,
  buttonContainer,
  skipCheckBox,
} from "./style_vanilla.css";
import {
  type DocumentsMap,
  getDocumentKey,
  prepareDocumentsForUpload,
  mapGuestDocumentsToFormProps,
} from "./utils";
import type { DocumentCollectorGuestProps } from "../DocumentCollectorGuest/types";

export const DocumentCollectorGuestv2 = ({
  documentUploadInviteDetails,
  token,
}: DocumentCollectorGuestProps) => {
  const [showCompleted, setShowCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentDetailsType | null>(null);
  const [shouldSkipDocuments, setShouldSkipDocuments] = useState(false);
  const [documents, setDocuments] = useState<DocumentsMap>({});
  const { uploadDocuments, skipDocumentUpload, loading } = useGuestDocumentUpload(token);

  const documentTypes = useMemo(
    () => documentUploadInviteDetails?.rulesEngine?.documentTypes || [],
    [documentUploadInviteDetails?.rulesEngine?.documentTypes],
  );

  const documentListProps: DocumentListProps = useMemo(() => {
    const documentDetails = mapGuestDocumentsToFormProps(documentTypes);
    return {
      documentDetails: documentDetails.map((doc) => {
        const key = getDocumentKey(doc.baseType, doc.subType);
        const storedDoc = documents[key];
        const fileToShow = storedDoc?.file ?? doc.file;
        return {
          ...doc,
          file: fileToShow,
          lastUploadedDate: doc.lastUploadedDate ? formatDate(doc.lastUploadedDate) : undefined,
          status: getDocumentStatus(!!fileToShow, doc.expiryDate, doc.uploaderInfo),
          onClick: () => setSelectedDocument({ ...doc, file: fileToShow }),
        };
      }),
    };
  }, [documentTypes, documents]);

  const hasUploadedDocuments = useMemo(() => {
    return (documentUploadInviteDetails?.uploadedDocuments?.length || 0) > 0;
  }, [documentUploadInviteDetails?.uploadedDocuments]);

  const hasDocuments = Object.keys(documents).length > 0;

  const handleSaveDocument = useCallback(
    (file: FileObjectType | null) => {
      if (!selectedDocument) {
        setSelectedDocument(null);
        return;
      }

      const key = getDocumentKey(selectedDocument.baseType, selectedDocument.subType);

      setDocuments((prev) => {
        if (!file) {
          return Object.fromEntries(
            Object.entries(prev).filter(([k]) => k !== key),
          ) as typeof prev;
        }

        return {
          ...prev,
          [key]: { ...selectedDocument, file },
        };
      });

      if (file) {
        setShouldSkipDocuments(false);
      }

      setSelectedDocument(null);
    },
    [selectedDocument],
  );

  const handleSubmit = useCallback(async () => {
    if (!token) {
      toast.error("No token found in URL");
      return;
    }

    setIsSubmitting(true);
    try {
      const documentsToUpload = prepareDocumentsForUpload(documents);
      if (documentsToUpload.length > 0) {
        await uploadDocuments(documentsToUpload);
      } else {
        await skipDocumentUpload(token);
      }
      toast.success("Documents submitted successfully");
      setShowCompleted(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }, [skipDocumentUpload, token, documents, uploadDocuments]);

  useEffect(() => {
    const filteredDocuments = documentTypes.filter(
      (docType) => docType.status !== DocumentStatus.Uploaded,
    );

    if (filteredDocuments.length === 0) {
      setShowCompleted(true);
      return;
    }
    setShowCompleted(
      documentUploadInviteDetails?.status === DocumentUploadInviteStatus.UploadCompleted,
    );
  }, [documentUploadInviteDetails, documentTypes]);

  if (showCompleted) {
    return <Celebration />;
  }

  if (selectedDocument) {
    return (
      <Card className={mainContentWrapper}>
        <DocumentUploader
          backNavText={`Documents for ${documentUploadInviteDetails?.complianceProfile?.profileName || ""}`}
          document={selectedDocument}
          isSubmitting={isSubmitting}
          onNavigateBack={() => setSelectedDocument(null)}
          onSave={(document) => {
            handleSaveDocument(document.file);
          }}
        />
      </Card>
    );
  }

  return (
    <Card
      className={mainContentWrapper}
      title={`Documents for ${documentUploadInviteDetails?.complianceProfile?.profileName}`}
    >
      <Text
        variant="regular-md"
        className={textColorNeutral800}
      >{`${documentUploadInviteDetails?.subscriberName} (${documentUploadInviteDetails?.subscriberEmailAddress}) has invited you to provide some information.`}</Text>

      <Flex className={inviteSummaryWrapper}>
        <Flex justifyContent="space-between" gap={Sizes.XS}>
          <Text variant="regular-md" lines={1} className={textColorNeutral900}>
            Message from
          </Text>
          <Text variant="strong-md" lines={1} className={textColorNeutral900}>
            {documentUploadInviteDetails?.subscriberName}
          </Text>
          <Text variant="regular-sm" className={dateDisplay}>
            {formatDate(documentUploadInviteDetails?.emailInviteTimestamp)}
          </Text>
        </Flex>
        <Text variant="regular-md" className={textColorNeutral700}>
          {documentUploadInviteDetails?.message}
        </Text>
      </Flex>

      <Flex justifyContent="flex-start" className={skipCheckBox}>
        <Checkbox
          checked={shouldSkipDocuments}
          onChange={(e) => setShouldSkipDocuments(e.target.checked)}
          disabled={hasDocuments}
          label="I cannot provide any of the documents listed below"
        />
      </Flex>

      <DocumentLists {...documentListProps} />

      <Flex className={buttonContainer}>
        <ButtonWrapper
          tooltip="Please upload all required documents before completing this section."
          id="help_indicator"
          disabled={!shouldSkipDocuments && !hasDocuments}
        >
          <Button
            variant="primary"
            onClick={() => {
              void handleSubmit();
            }}
            isLoading={isSubmitting}
            disabled={
              isSubmitting ||
              loading ||
              (!shouldSkipDocuments && !hasUploadedDocuments && !hasDocuments)
            }
            data-testid="complete-button"
          >
            Submit
          </Button>
        </ButtonWrapper>
      </Flex>
    </Card>
  );
};
